/** Log types hidden from customer track/trip timelines (mission control keeps full audit). */
const HIDDEN_LOG_TYPES = new Set(["Reviewed Documents"]);

export interface DeliveryLogEntry {
  log_type?: string;
  description?: string;
  log_time?: string;
}

export function filterCustomerDeliveryLogs(logs: unknown): DeliveryLogEntry[] {
  if (!Array.isArray(logs)) return [];
  return logs.filter((log) => !HIDDEN_LOG_TYPES.has(log?.log_type));
}

/** The one field on a pricing change a customer actually pays. */
const CUSTOMER_FACING_PRICING_FIELD = "customer_amount";

interface PricingChange {
  field?: string;
  label?: string;
  from?: number | null;
  to?: number | null;
}

function fmtKes(v: number | null | undefined): string {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `KES ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/**
 * Human label for one timeline entry on a customer-facing page.
 *
 * Three shapes reach this:
 *
 *  1. A JSON envelope from orders-service createLogDescription (manual
 *     pricing, 2026-09 onward). This was previously dumped to the page
 *     verbatim — the whole blob, braces and all, including the internal
 *     actor_staff_id — because the legacy "... by <someone>" regex below
 *     found no match and fell through to returning the raw string. Visible
 *     on the public tracking page at new.xobo.co.ke.
 *
 *  2. Legacy "<action> by <someone>" text, trimmed at " by" so staff and
 *     driver identities stay out of a customer's view.
 *
 *  3. Anything else, returned as-is.
 *
 * Deliberately narrow about what a pricing change tells a customer: the
 * amount THEY pay, and nothing else. Driver pay, commission and the
 * reviewer's internal reason note are Ops detail and stay in Mission
 * Control. The server also strips actor and reason for non-staff viewers;
 * this does not rely on that, because a page that renders identity when
 * the server forgets to remove it is a page that leaks identity.
 */
export function customerActivityLabel(log: DeliveryLogEntry): string {
  if (log.log_type === "Dropped") return "Order delivered";

  const desc = String(log.description ?? "");

  if (desc.startsWith("{")) {
    const fromJson = pricingLabel(desc);
    // Never fall through to returning the raw string for something that
    // looked like JSON — an unparseable blob is still a blob on screen.
    return fromJson ?? "Price updated";
  }

  const match = desc.match(/^(.*?)\s+by/i);
  return match ? match[1].trim() : desc;
}

function pricingLabel(raw: string): string | null {
  let parsed: any;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  if (parsed.kind !== "manual_pricing") return null;

  const changes: PricingChange[] = Array.isArray(parsed.changes) ? parsed.changes : [];
  const mine = changes.find((c) => c.field === CUSTOMER_FACING_PRICING_FIELD);

  // Only the customer's own total is worth naming. If the change touched
  // just internal figures, say that something changed without itemising
  // Xobo's commission to the customer.
  if (mine && mine.to != null) {
    return mine.from != null
      ? `Price updated · ${fmtKes(mine.from)} → ${fmtKes(mine.to)}`
      : `Price updated · ${fmtKes(mine.to)}`;
  }
  return "Price updated";
}
